import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DocumentHistoryRow,
  type DocumentHistoryRowActions,
} from "@/domains/documents/components/DocumentHistoryRow";
import type { DocumentHistoryEntry } from "@/domains/documents/history/documentHistory";
import { groupDocumentHistoryByDay } from "@/domains/documents/history/documentHistoryPresentation";
import type { useDocumentHistory } from "@/domains/documents/hooks/useDocumentHistory";
import type { DocumentTranslations } from "@/lib/i18n/types/documents";

type DocumentHistoryQuery = ReturnType<typeof useDocumentHistory>;
type DocumentHistoryCopy = DocumentTranslations["log"]["history"];

export interface DocumentHistoryPanelView {
  entries: DocumentHistoryEntry[];
  filteredEntries: DocumentHistoryEntry[];
  hasFilters: boolean;
  scopeUnavailable: boolean;
  projectScoped: boolean;
  pendingRestoreId: string | null;
}

export interface DocumentHistoryPanelActions {
  row: DocumentHistoryRowActions;
  clearFilters: () => void;
}

interface PanelBodyProps {
  history: DocumentHistoryQuery;
  view: DocumentHistoryPanelView;
  language: string;
  copy: DocumentHistoryCopy;
  actions: DocumentHistoryPanelActions;
}

function HistorySkeleton(props: { label: string }) {
  return (
    <div className="divide-y" role="status" aria-label={props.label}>
      {Array.from({ length: 5 }, (_, index) => (
        <div key={index} className="flex gap-3 px-5 py-4">
          <Skeleton className="h-9 w-9 flex-none rounded-full" />
          <div className="min-w-0 flex-1 space-y-2">
            <div className="flex justify-between gap-4">
              <Skeleton className="h-4 w-36" />
              <Skeleton className="h-3 w-14" />
            </div>
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-2/3" />
            <Skeleton className="h-3 w-40" />
          </div>
        </div>
      ))}
    </div>
  );
}

function HistoryMessage(props: {
  message: string;
  role: "status" | "alert";
  action?: { label: string; onClick: () => void };
}) {
  return (
    <div className="flex min-h-48 flex-col items-center justify-center gap-3 px-8 py-10 text-center">
      <p className="text-sm text-muted-foreground" role={props.role}>
        {props.message}
      </p>
      {props.action ? (
        <Button variant="outline" size="sm" onClick={props.action.onClick}>
          {props.action.label}
        </Button>
      ) : null}
    </div>
  );
}

function HistoryRows(props: {
  entries: DocumentHistoryEntry[];
  language: string;
  copy: DocumentHistoryCopy;
  pendingRestoreId: string | null;
  actions: DocumentHistoryRowActions;
}) {
  const groups = groupDocumentHistoryByDay(props.entries, props.language, props.copy.dayGroups);
  return (
    <div role="list" aria-label={props.copy.timelineLabel}>
      {groups.map((group) => (
        <section key={group.key} aria-labelledby={`document-history-day-${group.key}`}>
          <h3
            id={`document-history-day-${group.key}`}
            className="sticky top-0 z-20 border-b bg-background/95 px-5 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground backdrop-blur"
          >
            {group.label}
          </h3>
          <ul>
            {group.entries.map((entry) => (
              <DocumentHistoryRow
                key={entry.id}
                entry={entry}
                language={props.language}
                copy={props.copy}
                pendingRestore={props.pendingRestoreId === entry.documentId}
                actions={props.actions}
              />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

// The filters only see the pages loaded so far. While older pages remain, an
// empty result is not "nothing matches" — say so, and point at Load older.
function FilteredEmptyMessage(props: Pick<PanelBodyProps, "copy" | "actions" | "history">) {
  const message = props.history.hasNextPage
    ? props.copy.filteredEmptyLoaded
    : props.copy.filteredEmpty;
  return (
    <HistoryMessage
      role="status"
      message={message}
      action={{ label: props.copy.clearFilters, onClick: props.actions.clearFilters }}
    />
  );
}

function EmptyPanelBody(props: PanelBodyProps) {
  const { history, view, copy } = props;
  if (history.isError) {
    return (
      <HistoryMessage
        role="alert"
        message={copy.loadError}
        action={{ label: copy.retry, onClick: () => void history.refetch() }}
      />
    );
  }
  if (view.scopeUnavailable) return <HistoryMessage role="alert" message={copy.loadError} />;
  if (history.isPending) return <HistorySkeleton label={copy.loading} />;
  if (view.hasFilters)
    return <FilteredEmptyMessage copy={copy} actions={props.actions} history={history} />;
  if (!history.hasNextPage) {
    const message = view.projectScoped ? copy.emptyProject : copy.emptyBuilding;
    return <HistoryMessage role="status" message={message} />;
  }
  return null;
}

function PanelBody(props: PanelBodyProps) {
  const { history, view, copy } = props;
  if (view.entries.length === 0) return <EmptyPanelBody {...props} />;
  if (view.hasFilters && view.filteredEntries.length === 0) {
    return <FilteredEmptyMessage copy={copy} actions={props.actions} history={history} />;
  }
  return (
    <HistoryRows
      entries={view.filteredEntries}
      language={props.language}
      copy={copy}
      pendingRestoreId={view.pendingRestoreId}
      actions={props.actions.row}
    />
  );
}

function PanelFooter(props: {
  history: DocumentHistoryQuery;
  shownCount: number;
  loadedCount: number;
  copy: DocumentHistoryCopy;
}) {
  const { history, loadedCount, copy } = props;
  const knownTotal = `${loadedCount}${history.hasNextPage ? "+" : ""}`;
  return (
    <footer className="flex flex-wrap items-center justify-between gap-3 border-t bg-background px-5 py-3">
      <span className="text-xs font-medium tabular-nums text-muted-foreground">
        {copy.eventCount
          .replace("{shown}", String(props.shownCount))
          .replace("{total}", knownTotal)}
      </span>
      <div className="flex flex-wrap items-center justify-end gap-2">
        {history.isError && loadedCount > 0 ? (
          <span className="text-xs text-red-700" role="alert">
            {copy.loadError}
          </span>
        ) : null}
        {history.hasNextPage ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={history.isFetchingNextPage}
            onClick={() => void history.fetchNextPage()}
          >
            {history.isFetchingNextPage ? (
              <Loader2 className="animate-spin" aria-hidden="true" />
            ) : null}
            {history.isFetchingNextPage ? copy.loadingOlder : copy.loadOlder}
          </Button>
        ) : null}
        {history.isError && loadedCount > 0 ? (
          <Button type="button" variant="outline" size="sm" onClick={() => void history.refetch()}>
            {copy.retry}
          </Button>
        ) : null}
      </div>
    </footer>
  );
}

export function DocumentHistoryPanelBody(props: PanelBodyProps) {
  return (
    <>
      <div className="min-h-0 flex-1 overflow-y-auto" aria-busy={props.history.isFetching}>
        <PanelBody {...props} />
      </div>
      <PanelFooter
        history={props.history}
        shownCount={props.view.filteredEntries.length}
        loadedCount={props.view.entries.length}
        copy={props.copy}
      />
    </>
  );
}
